import { useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";
import { ApiError } from "@/lib/apiClient";
import { toastApiError } from "@/lib/toast";
import { toApiDateTime } from "@/utils/dates";
import { MerchantSelectField } from "@/features/merchants/components/MerchantSelectField";
import { tickets as ticketsApi } from "@/features/tickets/api/tickets.api";
import {
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_OPTIONS,
} from "@/features/tickets/components/TicketBadges";
import { AssigneeField } from "@/features/tickets/components/AssigneeField";
import { TICKET_MAIN_CATEGORY_OPTIONS, ticketCategoryOptions } from "@/features/tickets/categories";
import { backOrigin, useBackTo } from "@/hooks/useBackTo";
import { TemplateInsert } from "@/features/tickets/components/TemplateInsert";
import { appendMarkdown, type InlineImage } from "@/components/RichTextEditor";
import { useInlineImagePreview } from "@/hooks/useInlineImagePreview";
import { deflateInlineImages } from "@/utils/inlineImages";
import { Notice } from "@/components/Notice";
import { useConfirm } from "@/components/ConfirmDialog";
import { FEATURES } from "@/config/featureFlags";
import {
  AttachmentDropzone,
  validateFiles,
} from "@/features/tickets/components/TicketAttachments";
import { AttachmentGrid, AttachmentTile } from "@/components/AttachmentTile";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { TextField, SelectField, RichTextField, DateField, CheckField, useAutoFocus, useUnsavedChangesWarning } from "@/components/form";
import { Card, CardContent } from "@/components/ui/card";
import { Form } from "@/components/ui/form";

const schema = z.object({
  merchantId: z.string().min(1, "Please choose a merchant."),
  subject: z.string().trim().min(1, "A ticket subject is required.").max(FIELD_MAX.ticket.subject, tooLong("Subject", FIELD_MAX.ticket.subject)),
  status: z.string(),
  priority: z.string(),
  assignedToId: z.string(),
  mainCategory: z.string(),
  category: z.string(),
  body: z.string(),
  hideFromMerchant: z.boolean(),
  notifyMerchant: z.boolean(),
  dueAt: z.string(),
});

type FormValues = z.infer<typeof schema>;

export default function NewTicketPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetMerchantId = searchParams.get("merchantId") ?? "";
  const backTo = useBackTo("/tickets");

  const [loadError, setLoadError] = useState<string | null>(null);
  const [staged, setStaged] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [inlineImages, setInlineImages] = useState<InlineImage[]>([]);
  const [templateInsertedAt, setTemplateInsertedAt] = useState(0);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      merchantId: presetMerchantId,
      subject: "",
      status: "Open",
      priority: "Normal",
      assignedToId: "",
      mainCategory: "",
      category: "",
      body: "",
      hideFromMerchant: false,
      notifyMerchant: false,
      dueAt: "",
    },
  });

  useAutoFocus(form, presetMerchantId ? "subject" : "merchantId");

  const confirm = useConfirm();
  const dirty = form.formState.isDirty || staged.length > 0;
  useUnsavedChangesWarning(dirty);

  async function leaveBack(e: MouseEvent<HTMLAnchorElement>) {
    if (!dirty || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    const discard = await confirm({
      title: "Discard unsaved changes?",
      description: "This ticket has not been created yet. Leaving discards what you entered.",
      confirmLabel: "Discard",
      cancelLabel: "Stay",
      destructive: true,
    });
    if (discard) navigate(backTo);
  }

  function onMerchantError(e: unknown) {
    if (e instanceof ApiError && e.status === 403)
      setLoadError("You don't have permission to view merchants, which is required to raise a ticket.");
    else setLoadError(e instanceof Error ? e.message : "Failed to load merchants.");
  }

  const mainCategory = form.watch("mainCategory");
  const category = form.watch("category");
  const categoryOptions = useMemo(() => ticketCategoryOptions(mainCategory), [mainCategory]);

  useEffect(() => {
    if (!category) return;
    if (categoryOptions.some(([v]) => v === category)) return;
    form.setValue("category", "", { shouldDirty: true });
  }, [category, categoryOptions, form]);

  const idByUrl = useMemo(() => new Map(inlineImages.map((i) => [i.url, i.id])), [inlineImages]);
  const loadAttachment = useCallback((aid: string) => ticketsApi.attachmentFile(aid), []);
  const previewInlineImage = useInlineImagePreview(idByUrl, loadAttachment);

  async function uploadInlineImage(file: File) {
    const image = await ticketsApi.uploadInlineImage(null, file);
    setInlineImages((prev) => [...prev, image]);
    return image;
  }

  async function onSubmit(values: FormValues) {
    const body = deflateInlineImages(
      values.body,
      new Map(inlineImages.map((i) => [i.url, i.id])),
    );
    if (body.length > FIELD_MAX.ticket.body) {
      form.setError("body", {
        message: tooLong("Description", FIELD_MAX.ticket.body),
      });
      return;
    }
    try {
      const created = await ticketsApi.create({
        merchantId: values.merchantId,
        subject: values.subject.trim(),
        mainCategory: values.mainCategory.trim() || null,
        category: values.category.trim() || null,
        priority: values.priority,
        status: values.status,
        assignedToId: values.assignedToId || null,
        body: body.trim() || null,
        bodyFormat: "Markdown",
        hideFromMerchant: values.hideFromMerchant,
        notifyMerchant: values.notifyMerchant,
        dueAt: toApiDateTime(values.dueAt),
      });

      const failed: string[] = [];
      for (const file of staged) {
        try {
          await ticketsApi.uploadAttachment(created.id, file);
        } catch {
          failed.push(file.name);
        }
      }

      if (failed.length) {
        toast.warning(
          `Ticket created, but ${failed.length} attachment${failed.length === 1 ? "" : "s"} failed to upload: ${failed.join(", ")}`,
        );
      } else {
        toast.success("Ticket created");
      }
      navigate(`/tickets/${created.id}`, { state: backOrigin(backTo) });
    } catch (e2) {
      toastApiError(e2, "Failed to create the ticket.");
    }
  }

  function insertTemplate(content: string) {
    form.setValue("body", appendMarkdown(form.getValues("body"), content), {
      shouldDirty: true,
    });
    setTemplateInsertedAt((n) => n + 1);
  }

  function addFiles(files: File[]) {
    const { accepted, errors } = validateFiles(files, staged.length);
    setFileError(errors.length ? errors.join(" ") : null);
    if (accepted.length) setStaged((prev) => [...prev, ...accepted]);
  }

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-6">
        <Link to={backTo} onClick={leaveBack} className="text-sm text-muted-foreground hover:underline">
          ← Back
        </Link>
        <h1 className="text-2xl font-semibold text-slate-800 mt-1">New ticket</h1>
        <p className="text-sm text-slate-500 mt-1">
          Raise a support ticket against one of your merchants.
        </p>
      </header>

      {loadError && <Notice tone="error">{loadError}</Notice>}

      <Card>
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <MerchantSelectField
                control={form.control}
                name="merchantId"
                required
                onValueMissing={() => form.setValue("merchantId", "")}
                onError={onMerchantError}
              />

              <TextField control={form.control} name="subject" label="Subject" required placeholder="Short summary of the issue" />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <SelectField control={form.control} name="status" label="Status" options={TICKET_STATUS_OPTIONS} />
                <SelectField control={form.control} name="priority" label="Priority" options={TICKET_PRIORITY_OPTIONS} />
                <AssigneeField control={form.control} name="assignedToId" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <SelectField
                  control={form.control}
                  name="mainCategory"
                  label="Main category"
                  placeholder="Select a category…"
                  options={TICKET_MAIN_CATEGORY_OPTIONS}
                />
                <SelectField
                  control={form.control}
                  name="category"
                  label="Category"
                  placeholder={mainCategory ? "Select a category…" : "Select a main category first"}
                  options={categoryOptions}
                  searchable
                  disabled={categoryOptions.length === 0}
                />
              </div>

              <RichTextField
                control={form.control}
                name="body"
                label="Description"
                placeholder="Describe the issue in detail…"
                above={<TemplateInsert onInsert={insertTemplate} />}
                revealEndSignal={templateInsertedAt}
                uploadImage={uploadInlineImage}
                previewImage={previewInlineImage}
              />

              <div className="space-y-2">
                <Label>Attachments</Label>
                {fileError && <Notice tone="error">{fileError}</Notice>}
                <AttachmentDropzone
                  onFiles={addFiles}
                  disabled={form.formState.isSubmitting}
                  busy={form.formState.isSubmitting && staged.length > 0}
                />
                {staged.length > 0 && (
                  <AttachmentGrid>
                    {staged.map((f, i) => (
                      <AttachmentTile
                        key={`${f.name}-${i}`}
                        filename={f.name}
                        contentType={f.type}
                        sizeBytes={f.size}
                        disabled={form.formState.isSubmitting}
                        onRemove={() => setStaged((prev) => prev.filter((_, j) => j !== i))}
                      />
                    ))}
                  </AttachmentGrid>
                )}
              </div>

              <DateField control={form.control} name="dueAt" label="Due date" hint="Optional. Overdue open tickets get a red badge on the list." />

              {FEATURES.ticketMerchantOptions && (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <CheckField control={form.control} name="hideFromMerchant" label="Hide this ticket from the merchant" />
                  <CheckField control={form.control} name="notifyMerchant" label="Notify the merchant by email" />
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button asChild variant="ghost">
                  <Link to="/tickets">Cancel</Link>
                </Button>
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? "Creating…" : "Create ticket"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
